import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, money, newIdempotencyKey } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { fromMinorInput, toMinor } from './receipt-print';

export interface RefundablePayment {
  id: string;
  mode: string;
  amountMinor: number;
  refundableMinor: number;
  receiptNo: string | null;
}

interface ViewerInfo {
  viewer: { canApprove: boolean; approvalLimitMinor: number; unlimited: boolean; hasOpenShift: boolean; cashNeedsShift: boolean };
}

function deskRefundAllowed(amountMinor: number, overpaidMinor: number, paymentLeftMinor: number): boolean {
  return amountMinor <= overpaidMinor || amountMinor === paymentLeftMinor;
}

export function RequestRefundDialog({
  payment,
  invoiceNumber,
  overpaidMinor,
  creditNoteHref,
  onClose,
  onDone,
}: {
  payment: RefundablePayment;
  invoiceNumber: string;
  overpaidMinor: number;
  creditNoteHref: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState(fromMinorInput(payment.refundableMinor));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const info = useQuery({
    queryKey: ['refund-viewer'],
    queryFn: () => get<ViewerInfo>('/refunds?limit=1'),
    staleTime: 60_000,
  });

  const amountMinor = toMinor(amount);
  const viewer = info.data?.viewer;
  const within = viewer ? viewer.unlimited || (viewer.canApprove && amountMinor <= viewer.approvalLimitMinor) : false;
  const cashBlocked = payment.mode === 'cash' && within && viewer?.cashNeedsShift && !viewer.hasOpenShift;

  const request = useMutation({
    mutationFn: () => post<{ status: string }>('/refunds', { paymentId: payment.id, amountMinor, reason: reason.trim() }, { idempotencyKey }),
    onSuccess: (refund) => {
      if (refund.status === 'paid') toast.success(tr('billing', 'refund_approved_and_paid_out'));
      else if (refund.status === 'approved') toast.success(tr('billing', 'refund_approved_not_paid_out_yet'));
      else toast.success(tr('billing', 'refund_requested'), tr('billing', 'a_supervisor_will_decide_it'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const forServices = amountMinor > 0 && !deskRefundAllowed(amountMinor, overpaidMinor, payment.refundableMinor);
  const valid = amountMinor > 0 && amountMinor <= payment.refundableMinor && !forServices && reason.trim().length >= 5;

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('billing', 'refund_a_payment')}
      description={tr('billing', 'x_payment_of_x_on_x', { mode: humanize(payment.mode), amountMinor: money(payment.amountMinor), invoiceNumber: invoiceNumber })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              request.mutate();
            }}
            loading={request.isPending}
            disabled={!valid}
          >
            {within ? tr('billing', 'refund_now') : tr('billing', 'ask_for_approval')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('billing', 'amount_to_refund')} required hint={tr('billing', 'up_to_x_can_still_be_refunded_from_this_payment', { refundableMinor: money(payment.refundableMinor) })}>
          <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="tabular text-lg" autoFocus />
        </Field>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'at_least_five_characters_it_is_printed_on_the_2')}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
        </Field>

        {forServices && (
          <Alert variant="warning">
            <p>
              {overpaidMinor > 0
                ? tr('billing', 'only_x_was_paid_above_this_bill_a_refund_for', { overpaidMinor: money(overpaidMinor), paymentMinor: money(payment.refundableMinor) })
                : tr('billing', 'nothing_was_paid_above_this_bill_a_refund_for', { paymentMinor: money(payment.refundableMinor) })}
            </p>
            {creditNoteHref && (
              <Link to={creditNoteHref} className="mt-2 inline-block font-medium underline">
                {tr('billing', 'raise_a_credit_note_instead')}
              </Link>
            )}
          </Alert>
        )}
        {viewer && amountMinor > 0 && !forServices && (
          <Alert variant={within ? 'success' : 'warning'}>
            {within
              ? tr('billing', 'within_your_approval_limit_so_it_is_approved')
              : viewer.canApprove
                ? tr('billing', 'above_your_approval_limit_of_x_so_it_waits_for', { approvalLimitMinor: money(viewer.approvalLimitMinor) })
                : tr('billing', 'you_cannot_approve_refunds_yourself_so_it_waits')}
          </Alert>
        )}
        {cashBlocked && (
          <Alert variant="warning">
            {tr('billing', 'this_is_a_cash_refund_and_you_have_no_open')}
          </Alert>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
